import React, { useState, useCallback, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
} from "@material-ui/core";

import HeaderClientes from "components/_refactor/headers/clients";
import StructureFields from "components/_refactor/fields/structure";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import TableSemVenda from "./table/TableSemVenda";
import useForm from "hooks/form";
import componentStyles from "assets/theme/views/admin/react-bs-table";
import Api from "lib/api/httpRequestHelper";
import ClearButton from "components/_refactor/button/clear";
import LoadingData from "components/Loading/LoadingData";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import { IsMobile900, IsMobile } from "utils/is-mobile";
import InputText from "components/_refactor/text/input";

const useStyles = makeStyles(componentStyles);

const validateForm = (values) => {
  const errors = {};

  if (values.qntMaxPieces > 300) {
    errors.qntMaxPieces = "Valor não pode ser superior a 300";
  }

  if (values.qntPieces > 300) {
    errors.qntPieces = "Valor não pode ser superior a 300";
  }

  if (values.qntMaxPieces && values.qntPieces > values.qntMaxPieces) {
    errors.qntPieces = `Valor não pode ser maior que "Máximo de peças em estoque"`;
  }

  if (values.maxDays && values.minDays > values.maxDays) {
    errors.minDays = `Valor não pode ser maior que "Máximo de dias sem venda"`;
  }

  return errors;
};

function SemVenda() {
  const snackbar = useSnackbar();
  const classes = { ...useStyles() };

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  const [searching, setSearching] = useState(false);
  const [products, setProducts] = useState(null);
  const [selectedLine, setSelectedLine] = useState(null);
  const [structureFilters, setStructureFilters] = React.useState({});
  const [structureLoading, setStructureLoading] = React.useState([]);
  const [clearFilters, setClearFilters] = React.useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [hasNoBrand, setHasNoBrand] = useState(false);
  const [hasNoStore, setHasNoStore] = useState(false);
  const [hasNoLines, setHasNoLines] = useState(false);
  const [hasNoDays, setHasNoDays] = useState(false);
  const [storeListItens, setStoreListItens] = useState([]);

  const isMobile900 = IsMobile900();
  const isMobile = IsMobile();

  const itemXS = isMobile ? 12 : isMobile900 ? 6 : 3;

  const { values, handleChange, errors } = useForm(
    {
      statusProdutoCiclo: "",
      nomeColecao: "",
      minDays: "",
      maxDays: "",
      qntPieces: "",
      qntMaxPieces: "",
    },
    validateForm
  );

  const handleClearFilters = () => {
    setClearFilters(true);
    setSearching(true);
    setStructureLoading(true);
    setSelectedLine(null);
    handleChange({
      target: {
        name: "statusProdutoCiclo",
        value: "",
      },
    });
    handleChange({
      target: {
        name: "nomeColecao",
        value: "",
      },
    });
    handleChange({
      target: {
        name: "minDays",
        value: "",
      },
    });
    handleChange({
      target: {
        name: "maxDays",
        value: "",
      },
    });
    handleChange({
      target: {
        name: "qntPieces",
        value: "",
      },
    });
    handleChange({
      target: {
        name: "qntMaxPieces",
        value: "",
      },
    });

    setSearching(false);
    setStructureLoading(false);
  };

  const handleStopSearching = () => {
    setCancelLoading(true);
    setSearching(false);
    setProducts(null);
  };

  const search = useCallback(async () => {
    setSearching(true);
    setCancelLoading(false);
    setErrorMessage(false);

    const {
      statusProdutoCiclo = "",
      nomeColecao = "",
      minDays = 0,
      maxDays = 0,
      qntPieces = 0,
      qntMaxPieces = 0,
    } = values;

    await Api.get(
      "stock-product-without-sellout",
      "stock",
      "?idPessoa=" +
        structureFilters?.idLoja +
        "&idMarca=" +
        (structureFilters.idMarca ?? "TODAS").toString() +
        "&linha=" +
        (selectedLine?.Descricao || "") +
        (statusProdutoCiclo
          ? `&statusProdutoCiclo=${statusProdutoCiclo}`
          : "") +
        (nomeColecao ? `&nomeColecao=${nomeColecao}` : "") +
        "&qtdMinimaDiasSemVenda=" +
        minDays +
        (maxDays ? `&qtdMaximaDiasSemVenda=${maxDays}` : "") +
        (qntPieces ? `&qtdMinimaPecasEmEstoque=${qntPieces}` : "") +
        (qntMaxPieces ? `&qtdMaximaPecasEmEstoque=${qntMaxPieces}` : ""),
      true
    )
      .then((data) => {
        setProducts(data.Result);
      })
      .catch((error) => {
        console.log(error);
        setErrorMessage(true);      })
      .finally(() => setSearching(false));
  }, [
    selectedLine?.Descricao,
    structureFilters.idLoja,
    structureFilters.idMarca,
    values,
  ]);

  const handleVerifyRequiredInputs = () => {
    if (
      structureFilters.idLoja !== "" &&
      selectedLine &&
      selectedLine !== "all" &&
      values.minDays
    ) {
      return search();
    } else {
      if (!structureFilters.idLoja) {
        setHasNoStore(true);
      }

      if (!selectedLine || selectedLine === "all") {
        setHasNoLines(true);
      }

      if (!values.minDays) {
        setHasNoDays(true);
      }
    }
  };

  const fetchFields = useCallback(async () => {
    /** Busca marcas do usuário */
    await Api.get("product-category", "product", "", true)
      .then((value) => {
        setCategories(value.Result.ListaCategoria);
        setSubCategories(value.Result.ListaSubCategoria);
      })
      .catch((error) => {
        console.log(error);
        snackbar.showMessage(
          "Ocorreu um erro ao buscar categorias e subcategorias do usuário. Tente novamente mais tarde.",
          {
            severity: "error",
          }
        );      })
      .finally(() => setSearching(false));
  }, [snackbar]);

  const onLineChange = useCallback((newValue) => {
    setSelectedLine(newValue);
  }, []);

  useEffect(() => {
    if (errorMessage && !cancelLoading) {
      snackbar.showMessage(
        "Ocorreu um erro ao buscar produtos. Verifique os parâmetros informados e/ou tente novamente mais tarde.",
        {
          severity: "error",
        }
      );
    }
  }, [cancelLoading, errorMessage]);

  useEffect(() => {
    if (structureFilters.idMarca) {
      setHasNoBrand(false);
    }
    if (structureFilters.idLoja !== "") {
      setHasNoStore(false);
    }
    if (selectedLine && selectedLine !== "all") {
      setHasNoLines(false);
    }

    if (values.minDays) {
      setHasNoDays(false);
    }

    if (
      (structureFilters?.idMarca === null &&
        structureFilters?.idCanal === "" &&
        structureFilters?.idRepresentante === "" &&
        structureFilters?.idGrupoEconomico === "") ||
      values.statusProdutoCiclo === ""
    ) {
      setClearFilters(false);
    }
  }, [structureFilters, values, selectedLine]);

  useEffect(() => {
    fetchFields();
  }, []);

  return (
    <>
      <HeaderClientes section="Produtos Sem Venda" subsection="Estoque" />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }}>
              <CardHeader
                className={classes.cardHeader}
                title="Filtros"
                titleTypographyProps={{
                  component: Box,
                  marginBottom: "0!important",
                  variant: "h3",
                }}
              ></CardHeader>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <StructureFields
                    fieldsOrder={["economicGroup", "store"]}
                    clearFilters={clearFilters}
                    onStructureFieldsChange={setStructureFilters}
                    disableAll={searching}
                    onStructureFieldsLoad={setStructureLoading}
                    requiredFields={["store"]}
                    hasNoBrand={hasNoBrand}
                    hasNoStore={hasNoStore}
                    setStoreList={setStoreListItens}
                  />
                  <Grid item xs={12}>
                    <Divider style={{ margin: "0.5rem 0" }} />
                  </Grid>

                  <Grid item xs={itemXS}>
                    <AutoCompleteField
                      label="Linha"
                      loading={!categories || clearFilters}
                      onChange={onLineChange}
                      value={selectedLine}
                      options={categories}
                      disabled={searching}
                      optionLabelKey="Descricao"
                      optionIdKey="Descricao"
                      required
                    />
                  </Grid>

                  <Grid item xs={itemXS}>
                    <InputText
                      label="Status Ciclo"
                      value={values.statusProdutoCiclo}
                      onChange={(e) => {
                        handleChange({
                          target: {
                            name: "statusProdutoCiclo",
                            value: e.target.value.slice(0, 50),
                          },
                        });
                      }}
                      disable={searching}
                    />
                  </Grid>

                  <Grid item xs={itemXS}>
                    <InputText
                      label="Nome Coleção"
                      value={values.nomeColecao}
                      onChange={(e) => {
                        handleChange({
                          target: {
                            name: "nomeColecao",
                            value: e.target.value.slice(0, 200),
                          },
                        });
                      }}
                      disable={searching}
                    />
                  </Grid>

                  <Grid item xs={itemXS}>
                    <InputText
                      label="Mínimo de dias sem venda"
                      type="number"
                      value={values.minDays}
                      onChange={(e) => {
                        handleChange({
                          target: {
                            name: "minDays",
                            value: e.target.value.slice(0, 5),
                          },
                        });
                      }}
                      required
                      disable={searching}
                      error={hasNoDays}
                      errorMessage={errors["minDays"]}
                    />
                  </Grid>

                  <Grid item xs={itemXS}>
                    <InputText
                      label="Máximo de dias sem venda"
                      type="number"
                      value={values.maxDays}
                      onChange={(e) => {
                        handleChange({
                          target: {
                            name: "maxDays",
                            value: e.target.value.slice(0, 5),
                          },
                        });
                      }}
                      disable={searching}
                      error={!!errors["maxDays"]}
                      errorMessage={errors["maxDays"]}
                    />
                  </Grid>

                  <Grid item xs={itemXS}>
                    <InputText
                      label="Mínimo de peças em estoque"
                      type="number"
                      value={values.qntPieces}
                      onChange={(e) => {
                        handleChange({
                          target: {
                            name: "qntPieces",
                            value: e.target.value.slice(0, 5),
                          },
                        });
                      }}
                      disable={searching}
                    />
                  </Grid>

                  <Grid item xs={itemXS}>
                    <InputText
                      label="Máximo de peças em estoque"
                      type="number"
                      value={values.qntMaxPieces}
                      onChange={(e) => {
                        handleChange({
                          target: {
                            name: "qntMaxPieces",
                            value: e.target.value.slice(0, 3),
                          },
                        });
                      }}
                      disable={searching}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={isMobile ? 12 : isMobile900 && 6}>
                    <Button
                      style={
                        isMobile || isMobile900
                          ? {
                              width: "100%",
                              marginBottom: "0.75rem",
                              marginTop: "0.75rem",
                            }
                          : { marginBottom: "0rem" }
                      }
                      variant="contained"
                      onClick={handleVerifyRequiredInputs}
                      disabled={
                        searching ||
                        Object.keys(errors).length > 0 ||
                        structureLoading.length > 0
                      }
                    >
                      {searching ? "Pesquisando..." : "Pesquisar"}
                    </Button>
                  </Grid>
                  <Grid
                    item
                    xs={isMobile ? 12 : isMobile900 && 6}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <ClearButton
                      disable={searching || structureLoading.length > 0}
                      onClick={() => handleClearFilters()}
                      fullWidth={true}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <LoadingData
              searching={searching}
              onClick={() => handleStopSearching()}
              classes={classes.root}
              label="Buscando dados..."
            />
          </Grid>
        </Grid>
        {!searching && products && !cancelLoading && (
          <TableSemVenda dataTable={products} />
        )}
      </Container>
    </>
  );
}

export default SemVenda;
